import React, { useMemo, useRef, useEffect, useState } from "react";

// Anatomy region mapping for cluster positioning
const ANATOMY_REGIONS = {
    brain: { x: 0.5, y: 0.12, label: "Brain" },
    head: { x: 0.5, y: 0.15, label: "Head & Neck" },
    neck: { x: 0.5, y: 0.22, label: "Neck" },
    chest: { x: 0.5, y: 0.35, label: "Chest" },
    lung: { x: 0.35, y: 0.35, label: "Lung" },
    heart: { x: 0.65, y: 0.35, label: "Heart" },
    cardiac: { x: 0.65, y: 0.38, label: "Cardiac" },
    abdomen: { x: 0.5, y: 0.52, label: "Abdomen" },
    liver: { x: 0.62, y: 0.48, label: "Liver" },
    kidney: { x: 0.38, y: 0.55, label: "Kidney" },
    pancreas: { x: 0.5, y: 0.48, label: "Pancreas" },
    spleen: { x: 0.3, y: 0.48, label: "Spleen" },
    pelvis: { x: 0.5, y: 0.68, label: "Pelvis" },
    prostate: { x: 0.5, y: 0.72, label: "Prostate" },
    colon: { x: 0.5, y: 0.62, label: "Colon" },
    spine: { x: 0.5, y: 0.45, label: "Spine" },
    bone: { x: 0.15, y: 0.5, label: "Bone" },
    extremity: { x: 0.12, y: 0.7, label: "Extremity" },
    whole_body: { x: 0.5, y: 0.5, label: "Whole Body" },
    multiple: { x: 0.85, y: 0.5, label: "Multiple" },
    other: { x: 0.85, y: 0.25, label: "Other" },
};

// Modality offsets within anatomy clusters
const MODALITY_OFFSETS = {
    CT: { dx: 0, dy: 0 },
    MRI: { dx: 0.04, dy: 0.02 },
    MR: { dx: 0.04, dy: 0.02 },
    "X-ray": { dx: -0.04, dy: 0.02 },
    XR: { dx: -0.04, dy: 0.02 },
    PET: { dx: 0.04, dy: -0.03 },
    Ultrasound: { dx: -0.04, dy: -0.03 },
    US: { dx: -0.04, dy: -0.03 },
};

// Calculate star size based on dataset size or annotation count
function calculateStarSize(study) {
    const totalData = study.datasets_info?.reduce((acc, d) => {
        return acc + (d.patient_count || 0) + (d.image_count || 0);
    }, 0) || 0;

    const annotations = study.annotation_stats?.total_annotations || 0;

    // Scale: min 5px, max 14px
    const size = Math.min(14, Math.max(5, 5 + Math.log2(totalData + annotations + 1) * 1.5));
    return size;
}

// Get primary anatomy from study datasets
function getPrimaryAnatomy(study) {
    const anatomies = [];
    study.datasets_info?.forEach(d => {
        if (d.anatomies) anatomies.push(...d.anatomies);
    });

    if (anatomies.length === 0) return "other";
    if (anatomies.length > 2) return "multiple";

    const anatomy = (anatomies[0] || "").toLowerCase();

    // Match to known regions
    for (const key of Object.keys(ANATOMY_REGIONS)) {
        if (anatomy.includes(key)) return key;
    }
    return "other";
}

// Get primary modality from study datasets
function getPrimaryModality(study) {
    const modalities = [];
    study.datasets_info?.forEach(d => {
        if (d.modalities) modalities.push(...d.modalities);
    });
    return modalities[0] || "CT";
}

// Calculate star position with semi-random offset
function calculateStarPosition(study, index, total) {
    const anatomy = getPrimaryAnatomy(study);
    const modality = getPrimaryModality(study);

    const region = ANATOMY_REGIONS[anatomy] || ANATOMY_REGIONS.other;
    const modalityOffset = MODALITY_OFFSETS[modality] || { dx: 0, dy: 0 };

    // Add semi-random offset based on study id for consistent positioning
    const seed = study.id * 9301 + 49297;
    const randomX = ((seed % 233280) / 233280 - 0.5) * 0.1;
    const randomY = (((seed * 7) % 233280) / 233280 - 0.5) * 0.1;

    return {
        x: Math.max(0.05, Math.min(0.95, region.x + modalityOffset.dx + randomX)),
        y: Math.max(0.08, Math.min(0.92, region.y + modalityOffset.dy + randomY)),
    };
}

// Find related studies (same anatomy or modality)
function findRelatedStudies(study, allStudies) {
    const anatomy = getPrimaryAnatomy(study);
    const modality = getPrimaryModality(study);

    return allStudies.filter(s => {
        if (s.id === study.id) return false;
        const sAnatomy = getPrimaryAnatomy(s);
        const sModality = getPrimaryModality(s);
        return sAnatomy === anatomy || sModality === modality;
    });
}

// Generate 4-point star polygon points
function getStarPoints(cx, cy, size) {
    const outerRadius = size;
    const innerRadius = size * 0.4;
    const points = [];

    for (let i = 0; i < 8; i++) {
        const angle = (i * Math.PI) / 4 - Math.PI / 2; // Start from top
        const radius = i % 2 === 0 ? outerRadius : innerRadius;
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        points.push(`${x},${y}`);
    }

    return points.join(" ");
}

const StarConstellation = ({
    studies,
    selectedStudy,
    hoveredStudy,
    onStarClick,
    onStarHover,
    onStarLeave,
}) => {
    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 600, height: 500 });

    // Update dimensions on resize
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setDimensions({
                    width: rect.width || 600,
                    height: rect.height || 500,
                });
            }
        };

        updateDimensions();
        window.addEventListener("resize", updateDimensions);
        return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    // Calculate star positions
    const starPositions = useMemo(() => {
        return studies.map((study, index) => {
            const pos = calculateStarPosition(study, index, studies.length);
            return {
                study,
                x: pos.x * dimensions.width,
                y: pos.y * dimensions.height,
                size: calculateStarSize(study),
            };
        });
    }, [studies, dimensions]);

    // Calculate constellation lines for hovered/selected study
    const constellationLines = useMemo(() => {
        const activeStudy = hoveredStudy || selectedStudy;
        if (!activeStudy) return [];

        const relatedStudies = findRelatedStudies(activeStudy, studies);
        const activePos = starPositions.find(sp => sp.study.id === activeStudy.id);
        if (!activePos) return [];

        return relatedStudies.map(related => {
            const relatedPos = starPositions.find(sp => sp.study.id === related.id);
            if (!relatedPos) return null;
            return {
                x1: activePos.x,
                y1: activePos.y,
                x2: relatedPos.x,
                y2: relatedPos.y,
                relatedId: related.id,
            };
        }).filter(Boolean);
    }, [hoveredStudy, selectedStudy, studies, starPositions]);

    // Get unique anatomy regions that have studies
    const activeRegions = useMemo(() => {
        const regions = new Set();
        studies.forEach(study => {
            regions.add(getPrimaryAnatomy(study));
        });
        return Array.from(regions);
    }, [studies]);

    return (
        <div ref={containerRef} className="constellation-container">
            <svg
                className="constellation-svg"
                viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                preserveAspectRatio="xMidYMid meet"
            >
                {/* Gradient definitions */}
                <defs>
                    <radialGradient id="starGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(255, 255, 255, 0.9)" />
                        <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
                    </radialGradient>
                    <radialGradient id="starGlowHover" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(255, 255, 255, 1)" />
                        <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
                    </radialGradient>
                    <radialGradient id="starGlowSelected" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(255, 255, 255, 1)" />
                        <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
                    </radialGradient>
                </defs>

                {/* Constellation lines */}
                <g className="constellation-lines">
                    {constellationLines.map((line, idx) => (
                        <line
                            key={`line-${idx}`}
                            x1={line.x1}
                            y1={line.y1}
                            x2={line.x2}
                            y2={line.y2}
                            className="constellation-line"
                        />
                    ))}
                </g>

                {/* Region labels (subtle) */}
                <g className="region-labels">
                    {activeRegions.map(regionKey => {
                        const region = ANATOMY_REGIONS[regionKey];
                        if (!region) return null;
                        return (
                            <text
                                key={regionKey}
                                x={region.x * dimensions.width}
                                y={region.y * dimensions.height - 25}
                                className="region-label"
                            >
                                {region.label}
                            </text>
                        );
                    })}
                </g>

                {/* Stars */}
                <g className="stars">
                    {starPositions.map(({ study, x, y, size }) => {
                        const isSelected = selectedStudy?.id === study.id;
                        const isHovered = hoveredStudy?.id === study.id;
                        const isRelated = constellationLines.some(
                            l => l.relatedId === study.id
                        );

                        return (
                            <g
                                key={study.id}
                                className={`star-group ${isSelected ? "selected" : ""} ${
                                    isHovered ? "hovered" : ""
                                } ${isRelated ? "related" : ""}`}
                                onClick={() => onStarClick(study)}
                                onMouseEnter={() => onStarHover(study)}
                                onMouseLeave={onStarLeave}
                                style={{ cursor: "pointer" }}
                            >
                                {/* Outer glow */}
                                <circle
                                    cx={x}
                                    cy={y}
                                    r={size * 3}
                                    className="star-glow-outer"
                                    fill={isSelected ? "url(#starGlowSelected)" : isHovered ? "url(#starGlowHover)" : "url(#starGlow)"}
                                />
                                {/* Inner glow */}
                                <circle
                                    cx={x}
                                    cy={y}
                                    r={size * 1.8}
                                    className="star-glow"
                                />
                                {/* Star shape - 4-point star */}
                                <polygon
                                    points={getStarPoints(x, y, size)}
                                    className="star-shape"
                                />
                            </g>
                        );
                    })}
                </g>

                {/* Tooltip for hovered star */}
                {hoveredStudy && (() => {
                    const pos = starPositions.find(sp => sp.study.id === hoveredStudy.id);
                    if (!pos) return null;

                    // Adjust tooltip position to stay within bounds
                    const tooltipX = pos.x + 20 + 180 > dimensions.width
                        ? pos.x - 190
                        : pos.x + 20;
                    const tooltipY = pos.y - 35 < 0 ? pos.y + 10 : pos.y - 35;

                    return (
                        <foreignObject
                            x={tooltipX}
                            y={tooltipY}
                            width="180"
                            height="65"
                            className="star-tooltip"
                        >
                            <div className="tooltip-content">
                                <strong>{hoveredStudy.name}</strong>
                                <small>
                                    {hoveredStudy.datasets_info?.length || 0} dataset(s) | {" "}
                                    {hoveredStudy.annotation_stats?.total_annotations || 0} annotations
                                </small>
                            </div>
                        </foreignObject>
                    );
                })()}
            </svg>
        </div>
    );
};

export default StarConstellation;
